/**
 * Article rédigé le 2026-09-06 à partir de sources web ouvertes et vérifiées automatiquement
 * (6 URL testées, révisé après re-vérification). Daté au moment des faits : 2025-10-21.
 * À relire avant/après mise en ligne. Format : voir src/blog/README.md
 */
export default {
  "slug": "hacking-health-besancon-2025-nouvel-ifps",
  "title": "Hacking Health Besançon : 48 heures d'innovation en santé au nouvel IFPS",
  "excerpt": "Du 17 au 19 octobre 2025, la 9e édition du marathon d’innovation en santé était annoncée dans le nouveau bâtiment de formation des professionnels de santé, sans classement ni évaluation.",
  "seoTitle": "Hacking Health : 48 h d’innovation en santé",
  "seoDescription": "Du 17 au 19 octobre 2025, la 9e édition du marathon d’innovation en santé réunissait soignants, étudiants et développeurs au nouvel IFPS Paulette Guinchard.",
  "category": "evenements",
  "tags": [
    "Hacking Health",
    "Le Tube à Essais",
    "IFPS Paulette Guinchard",
    "TEMIS Santé",
    "Santé numérique"
  ],
  "author": "jimmy",
  "publishedAt": "2025-10-21",
  "updatedAt": "2025-10-21",
  "featured": false,
  "cover": {
    "image": "/img/blog/junction-2015.jpg",
    "credit": "Photo : Junction 2015, participants d'un hackathon au travail — Vmuru, CC BY-SA 4.0, via Wikimedia Commons",
    "alt": "Équipes de participants réunies autour de longues tables de travail couvertes d'ordinateurs portables et de gobelets lors d'un hackathon, dans une grande salle éclairée en bleu dont les murs sont tapissés de feuilles de notes et de post-it colorés"
  },
  "content": [
    {
      "type": "lead",
      "text": "Le marathon d’innovation en santé Hacking Health Besançon était programmé du vendredi 17 au dimanche 19 octobre, pour sa neuvième édition, dans les locaux du nouvel Institut de formation des professions de santé (IFPS), que la presse locale désigne aussi comme l’« Institut de Formation des Professionnels de Santé »[^3]. Quarante-huit heures durant, le principe est de faire travailler ensemble des équipes mêlant soignants, étudiants, ingénieurs et développeurs sur des problématiques remontées du terrain, collectées tout au long de l’année auprès des établissements et associations de santé de Bourgogne-Franche-Comté[^1]. Sans classement ni évaluation[^1] : à Besançon, l’essentiel se joue après le week-end."
    },
    {
      "type": "keyfacts",
      "facts": [
        {
          "label": "Quoi",
          "value": "9e édition du Hacking Health Besançon, marathon d’innovation ouverte en santé de 48 heures"
        },
        {
          "label": "Quand",
          "value": "Du vendredi 17 au dimanche 19 octobre 2025"
        },
        {
          "label": "Où",
          "value": "Nouvel IFPS Paulette Guinchard, pôle TEMIS Santé, Besançon"
        },
        {
          "label": "Qui",
          "value": "Porté par l’équipe du Hacking Health Besançon, à l’origine de la société coopérative d’intérêt collectif Le Tube à Essais"
        },
        {
          "label": "Pour qui",
          "value": "Soignants, patients, aidants, étudiants, ingénieurs, designers et développeurs"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Quarante-huit heures dans un bâtiment tout neuf"
    },
    {
      "type": "p",
      "text": "L’édition était programmée dans les locaux du nouvel IFPS, aux dates annoncées dès le printemps — les 17, 18 et 19 octobre : « le rendez-vous se tiendra cette saison dans les locaux du nouvel IFPS », écrivait Hebdo25 en avril[^3][^2]. Le bâtiment, baptisé Paulette Guinchard et inauguré le 17 mars dernier, rassemble sur plus de 7 000 m² « l’ensemble des formations paramédicales et sociales » jusque-là « réparties sur plusieurs sites », pour près de 1 000 étudiants, au cœur du [pôle TEMIS Santé](/blog/besancon-terrain-naturel-ia-sante-chu-isifc-temis) et « en proximité immédiate du CHU Jean Minjoz et des infrastructures universitaires »[^6]. L’adresse change : l’édition précédente s’est tenue à l’UFR Santé[^5]."
    },
    {
      "type": "p",
      "text": "Le format, lui, n’a pas bougé. Hebdo25 titrait en avril sur « 300 participants pour donner vie à une vingtaine de défis »[^3] — un ordre de grandeur cohérent avec la présentation du marathon, qui accueille « chaque année près de 300 participants »[^1] —, le principe restant que « pendant 48 heures, des étudiants et professionnels venus d’horizons différents réfléchissent et prototypent ensemble »[^3]. Le lieu ajoute une cohérence supplémentaire : l’IFPS **réunit tous les futurs professionnels de santé**, « des infirmiers aux brancardiers, des kinés aux puéricultrices »[^3]."
    },
    {
      "type": "h2",
      "text": "Un marathon sans classement ni évaluation"
    },
    {
      "type": "p",
      "text": "Il faut le redire, car cela surprend les habitués [des hackathons](/blog/nuit-de-linfo-2025-besancon-85-etudiants) : [Hacking Health Besançon](https://letubeaessais.fr/nos-evenements/hacking-health-besancon/) n’est pas une compétition, et l’événement ne produit « ni classement ni évaluation »[^1]. Il se « conclu[t] par une cérémonie de clôture où toutes les solutions sont présentées »[^1]."
    },
    {
      "type": "p",
      "text": "En amont, les problématiques sont collectées au fil de l’année : l’équipe part régulièrement à la rencontre des établissements et associations de santé de Bourgogne-Franche-Comté[^1]. Le week-end suit ensuite un déroulé rodé[^1] :"
    },
    {
      "type": "ul",
      "items": [
        "une cérémonie d’ouverture, puis la composition des équipes autour des défis retenus ;",
        "une phase d’idéation encadrée par un facilitateur ;",
        "le passage de coachs et d’experts, et l’accès au fablab pour maquetter ;",
        "à l’arrivée, une **preuve de concept**, voire un pré-prototype, présentée lors de la clôture."
      ]
    },
    {
      "type": "p",
      "text": "Les profils appelés autour des tables disent quelque chose de l’époque : la page de présentation du marathon cite, parmi ceux que l’on y retrouve, « des médicaux et para-médicaux, des designers, des ingénieurs, des communicants, des développeurs web ou IA »[^1]. L’intelligence artificielle n’apparaît pas comme un thème affiché du marathon dans sa communication ; elle y figure d’abord comme une compétence parmi d’autres, mobilisée au service d’une gêne éprouvée par un soignant ou un patient."
    },
    {
      "type": "h2",
      "text": "L’essentiel se joue après le dimanche soir"
    },
    {
      "type": "p",
      "text": "La suite se déroule ailleurs, dans la structure née du marathon lui-même : la société coopérative **Le Tube à Essais**, créée en septembre 2024, dont la mission est « d’aider les projets à mûrir après le Hacking Health en accompagnant pendant un à deux ans le porteur »[^2]."
    },
    {
      "type": "p",
      "text": "L’entonnoir est assumé, et l’ordre de grandeur connu : au printemps dernier, la coopérative indiquait que « sur 12 projets soumis, huit ont été présélectionnés, et quatre sont actuellement accompagnés »[^2]. Autre signe de consolidation, un diplôme universitaire consacré à l’innovation en santé devait faire sa première rentrée en septembre : annoncé en avril sous le titre « Développer/accompagner l’innovation en santé », « en partenariat avec l’ISIFC »[^2], il est présenté par la coopérative comme « co-porté par l’ISIFC et l’UFR Santé de Besançon »[^1]."
    },
    {
      "type": "h2",
      "text": "Une édition dans la continuité de 2024"
    },
    {
      "type": "p",
      "text": "L’an dernier, la huitième édition s’est tenue du 18 au 20 octobre à l’UFR Santé, sur le campus des Hauts-du-Chazal[^5][^4]. Seize défis y avaient été lancés, dont un « co-animé en distanciel, avec une équipe de Québec », sur des thèmes allant de l’accessibilité au diabète en passant par l’impact de la santé sur l’environnement[^4]. D’un millésime à l’autre, le format ne bouge pas ; le nombre de défis effectivement retenus en 2025 n’était pas connu à l’heure où nous écrivons — les organisateurs en annonçaient « une vingtaine » au printemps[^3], contre seize lancés en 2024[^4]. Ce qui change surtout, c’est la profondeur du dispositif d’aval."
    },
    {
      "type": "callout",
      "tone": "info",
      "title": "Pourquoi ça compte pour Besançon",
      "text": "Une neuvième édition, ce n’est plus un événement isolé : c’est une habitude locale. Le marathon s’appuie sur une concentration d’acteurs à l’échelle de l’agglomération — un CHU, un pôle santé dédié, et depuis mars l’ensemble des formations paramédicales et sociales bisontines réunies sur un même site[^6]. Autour de lui s’est constitué un circuit — collecte des défis, week-end de prototypage, accompagnement en coopérative, diplôme universitaire[^1][^2] — qui donne aux idées venues du terrain une trajectoire possible au-delà du dimanche soir."
    },
    {
      "type": "h2",
      "text": "Ce qu’il faut retenir"
    },
    {
      "type": "p",
      "text": "La neuvième édition s’inscrit dans une mécanique qui ne produit pas de palmarès : le week-end n’est qu’une porte d’entrée, et l’absence de classement comme d’évaluation est assumée[^1]. Le bilan de cette cuvée ne se lira donc pas dimanche soir, mais dans les mois qui viennent, au fil des projets que Le Tube à Essais retiendra — quatre étaient accompagnés au printemps, sur douze soumis[^2]. Nous suivrons ce que deviennent les défis de 2025."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "Hacking Health Besançon — présentation du marathon d’innovation",
      "publisher": "Le Tube à Essais",
      "url": "https://letubeaessais.fr/nos-evenements/hacking-health-besancon/",
      "type": "official",
      "date": ""
    },
    {
      "id": 2,
      "title": "Hacking Health Besançon prépare sa 9e édition et renforce son écosystème d’innovation en santé",
      "publisher": "macommune.info",
      "url": "https://www.macommune.info/hacking-health-besancon-prepare-sa-9e-edition-et-renforce-son-ecosysteme-dinnovation-en-sante/",
      "type": "press",
      "date": "2025-04-11"
    },
    {
      "id": 3,
      "title": "Besançon. Le Hacking Health en route vers sa 9ème édition",
      "publisher": "Hebdo25",
      "url": "https://hebdo25.net/besancon-le-hacking-health-en-route-vers-sa-9eme-edition/",
      "type": "press",
      "date": "2025-04-25"
    },
    {
      "id": 4,
      "title": "Besançon / Santé : 8è Hacking Health",
      "publisher": "Plein Air",
      "url": "https://www.pleinair.net/actualites/item/13692-besancon-sante-8e-hacking-health",
      "type": "press",
      "date": "2024-10-17"
    },
    {
      "id": 5,
      "title": "Hacking Health Besançon 2024 — 8ème édition",
      "publisher": "Université Marie et Louis Pasteur",
      "url": "https://actu.umlp.fr/agenda/hacking-health-besancon-2024-8eme-edition",
      "type": "official",
      "date": ""
    },
    {
      "id": 6,
      "title": "L’IFPS Paulette Guinchard est inauguré",
      "publisher": "Région Bourgogne-Franche-Comté",
      "url": "https://www.bourgognefranchecomte.fr/lifps-paulette-guinchard-est-inaugure",
      "type": "official",
      "date": "2025-03-18"
    }
  ]
};
